// Web mirror of the monolith's persistent-table / floor-plan-area contracts (#201).
// Backend sources: API.Core/Models/Booking/Entities/Table/Table.cs (TableNl) and
// API.Core/Models/Booking/Entities/Area/Area.cs (AreaNl), served by TableController / AreaController under
// `/booking/{controller}/{action}`. See docs/articles/booking/table-selection-and-areas.md.
//
// Two wire facts that shape these types:
//  * The monolith serializes with Newtonsoft using a camelCase naming strategy but **no** StringEnumConverter —
//    so every C# enum rides the wire as its **numeric** value. The enums below therefore mirror the C# member
//    ORDER, not their names.
//  * `NullValueHandling.Ignore` is configured globally, so a null `areaId` / `idleMinutes` / `description` is
//    **omitted** from the response rather than sent as `null`. Everything read off the wire goes through
//    {@link normalizeTable} / {@link normalizeArea} so the rest of the module can rely on total objects.

import {polygonCentroid, polygonLabelAnchor} from './floor-plan-geometry';

/** Geometry of a table marker on the floor plan. Mirrors `API.Core...Table.TableShape`. */
export enum TableShape {
  Rectangle = 0,
  Circle = 1
}

/** Kind of table. Mirrors `API.Core...Table.TableType` (only one member today). */
export enum TableType {
  Normal = 0
}

/**
 * Derived occupancy of a persistent table. Mirrors `API.Core...Table.TableStatus` — deliberately two members
 * (reservations are out of scope for #201). Read-only: the backend derives it from the booking reference, so the
 * admin screen displays it but never edits it.
 */
export enum TableStatus {
  Free = 0,
  Occupied = 1
}

/** One vertex of an area polygon, in the same integer coordinate space as a table's `x`/`y`. */
export interface AreaNode {
  x: number;
  y: number;
}

/** A persistent table as returned by `GET /booking/Table/List` (`TableNl`). */
export interface FloorTable {
  id: string;
  /** The table's display number/name (`ModelBase.Name`). */
  name: string;
  tableType: TableType;
  tableShape: TableShape;
  seats: number;
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
  /**
   * `false` for every table this screen manages. Ad-hoc (`temporary === true`) tables belong to the separate
   * non-persistent booking flow, are filtered out of `List`, and are never created here.
   */
  temporary: boolean;
  /** The area this table is grouped into, or `null` when it is placed without one. */
  areaId: string | null;
  /** Derived, read-only. */
  status: TableStatus;
  /** Derived, read-only; `null` when the table is free (or occupied only by an expected booking). */
  idleMinutes: number | null;
}

/** A floor-plan area as returned by `GET /booking/Area/List` (`AreaNl`). */
export interface FloorArea {
  id: string;
  name: string;
  /** Ordered, implicitly closed polygon outline — the first vertex is NOT repeated at the end. */
  nodes: AreaNode[];
  /**
   * The area's display colour as `#RRGGBB`. The canvas fills the polygon with it at 40 %, strokes the outline with
   * it, and draws the centroid cross in it at 55 %.
   */
  color: string;
  /**
   * Horizontal coordinate of the area's middle point — the **area-weighted (shoelace) centroid**.
   *
   * **Read-only from here.** The backend computes this pair from `nodes` on every write (`AreaBase.RecomputeCenter`)
   * and its write models carry no such property at all, so sending one is impossible by construction. It is
   * mirrored client-side by {@link polygonCentroid} for a live preview of an area still being dragged; this stored
   * value is what the server persisted at the last save.
   */
  centerX: number;
  /** Vertical coordinate of the area's middle point. Server-computed — see {@link FloorArea.centerX}. */
  centerY: number;
  /**
   * Horizontal coordinate of the point the area's **name label** is drawn at — the polygon's **pole of
   * inaccessibility** (the centre of the largest circle that fits inside the outline).
   *
   * **A separate point from {@link FloorArea.centerX}, on purpose.** The centroid answers "where is the mass?" and
   * stays the middle-point *marker*; it is not guaranteed to lie inside a non-convex polygon, so a caption drawn
   * there floats outside the shape it names (a U-shaped bar counter's centroid sits in the notch between its
   * arms). This point is interior by construction.
   *
   * **Read-only from here**, exactly like the centroid: the backend derives it from `nodes` on every write
   * (`AreaBase.RecomputeDerivedPoints`) and its write models carry no such property. Mirrored client-side by
   * {@link polygonLabelAnchor} for the live preview of an area being dragged.
   */
  labelX: number;
  /** Vertical coordinate of the area's label anchor. Server-computed — see {@link FloorArea.labelX}. */
  labelY: number;
}

/**
 * A shard's floor-plan background image as returned by `GET /booking/FloorPlan/Get` (`FloorPlanBackgroundNl`) —
 * the scan or vector export the table layout is traced over. There is at most one per shard, so it is addressed
 * without an id.
 */
export interface FloorPlanBackground {
  id: string;
  /** The original file name of the upload. */
  fileName: string;
  /** The stored image's canonical MIME type, replayed by the download endpoint. */
  contentType: string;
  /** The stored image's size in bytes. */
  sizeBytes: number;
  /** Horizontal offset of the image's **top-left** corner, in floor-plan units. */
  x: number;
  /** Vertical offset of the image's **top-left** corner, in floor-plan units. */
  y: number;
  /**
   * Rendered width in floor-plan units. **`0` means "not placed yet"** — render at the image's natural width until
   * the admin resizes it once (see {@link backgroundWidthOrNatural}). The height is never stored: it follows from
   * this width and the decoded image's aspect ratio (see {@link backgroundHeight}).
   */
  width: number;
  /** Rendering opacity, `0`–`1`. A tracing aid is normally dialled well below 1. */
  opacity: number;
  /**
   * The **relative, authenticated** API route the bytes are fetched from (`/booking/FloorPlan/Image`).
   *
   * Not a public blob URL: the container is private and the endpoint is role-gated, so it must be fetched through
   * the app's HTTP client and turned into an object URL. A bare `<img src>` fires an unauthenticated request and
   * 401s (see {@link TableLayoutHttpService.downloadBackgroundImage}).
   */
  url: string;
}

/** Write model for `POST /booking/FloorPlan/Update` (`FloorPlanBackgroundUpdate`) — placement only, no new file. */
export interface BackgroundPlacementUpdate {
  x: number;
  y: number;
  width: number;
  opacity: number;
}

/**
 * Largest background image the editor uploads (10 MB), mirroring the backend's `FloorPlanImageFormats.MaxBytes`.
 * Checked client-side **before** the request so a 40 MB scan fails instantly with a readable message instead of
 * after a long upload that the server rejects anyway.
 */
export const BACKGROUND_MAX_BYTES = 10 * 1024 * 1024;

/** File extensions the background upload accepts, mirroring `FloorPlanImageFormats.Extensions`. */
export const BACKGROUND_EXTENSIONS: readonly string[] = ['.png', '.jpg', '.jpeg', '.svg'];

/** The `accept` attribute of the background file input, mirroring `FloorPlanImageFormats.ContentTypes`. */
export const BACKGROUND_ACCEPT = 'image/png,image/jpeg,image/svg+xml';

/**
 * Validates a picked background file the way the server will, so the failure is reported before the upload rather
 * than after it.
 *
 * @returns `null` when the file is acceptable, otherwise a ready-to-show German message.
 */
export function validateBackgroundFile(file: File): string | null {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  if (!BACKGROUND_EXTENSIONS.includes(extension)) {
    return 'Nur PNG-, JPG-, JPEG- oder SVG-Dateien können als Hintergrund verwendet werden.';
  }
  if (file.size > BACKGROUND_MAX_BYTES) {
    return `Das Bild ist zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB). Erlaubt sind maximal 10 MB.`;
  }
  return null;
}

/** Write model for `POST /booking/Table/UpsertList` (`TableUpdate`); a blank/absent `id` inserts. */
export interface TableUpdate {
  id?: string;
  name: string;
  tableType: TableType;
  tableShape: TableShape;
  seats: number;
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
  temporary: boolean;
  areaId: string | null;
}

/**
 * Write model for `POST /booking/Area/UpsertList` (`AreaUpdate`); a blank/absent `id` inserts.
 *
 * Deliberately carries **no** `centerX`/`centerY` or `labelX`/`labelY`: the backend derives both points from
 * `nodes` on every write and its input models have no such property to bind, so sending one would be silently
 * dropped at best.
 */
export interface AreaUpdate {
  id?: string;
  name: string;
  nodes: AreaNode[];
  /**
   * `#RRGGBB`, or **blank for "unspecified"** — the server then keeps the area's current colour (or defaults a new
   * one to the platform primary). Mirrors the blank-tolerant handling of the other optional fields on this wire.
   */
  color: string;
}

/**
 * Prefix marking an entity that exists only in the editor's draft and has never been persisted. The backend
 * decides ids, so a locally added table/area carries a synthetic one until the first successful save; anything
 * carrying this prefix is sent **without** an id so the upsert inserts instead of failing to match.
 */
export const DRAFT_ID_PREFIX = 'draft:';

/** Whether this id belongs to a draft (never-saved) entity. */
export function isDraftId(id: string): boolean {
  return id.startsWith(DRAFT_ID_PREFIX);
}

/** A fresh, collision-free draft id. */
export function newDraftId(): string {
  return `${DRAFT_ID_PREFIX}${Math.random().toString(36).slice(2, 10)}`;
}

/** Fills in the fields the monolith omits for null/absent values, so the editor works on total objects. */
export function normalizeTable(raw: Partial<FloorTable>): FloorTable {
  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    tableType: raw.tableType ?? TableType.Normal,
    tableShape: raw.tableShape ?? TableShape.Rectangle,
    seats: raw.seats ?? 0,
    x: raw.x ?? 0,
    y: raw.y ?? 0,
    // A zero-sized marker would be invisible and unclickable; fall back to the default footprint.
    width: raw.width && raw.width > 0 ? raw.width : DEFAULT_TABLE_SIZE,
    height: raw.height && raw.height > 0 ? raw.height : DEFAULT_TABLE_SIZE,
    description: raw.description ?? '',
    temporary: raw.temporary ?? false,
    areaId: raw.areaId ?? null,
    status: raw.status ?? TableStatus.Free,
    idleMinutes: raw.idleMinutes ?? null
  };
}

/** Fills in the fields the monolith omits for null/absent values. */
export function normalizeArea(raw: Partial<FloorArea>): FloorArea {
  const nodes = (raw.nodes ?? []).map(node => ({x: node.x ?? 0, y: node.y ?? 0}));
  // A blank colour is what a row written before this field existed looks like; it must still render.
  const color = isHexColor(raw.color) ? raw.color!.trim() : DEFAULT_AREA_COLOR;
  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    nodes,
    color,
    // Absent on a legacy row: fall back to the same centroid the server would compute, so the marker is never at
    // (0, 0) for an area that has simply not been re-saved yet.
    centerX: raw.centerX ?? polygonCentroid(nodes).x,
    centerY: raw.centerY ?? polygonCentroid(nodes).y,
    // Same treatment for the label anchor. The server already resolves a row that predates these columns during
    // projection (`AreaBase.CopyDerivedPoints`), so this is belt-and-braces for anything reading an older cached
    // payload — and it is the identical formula, so the two answers agree.
    labelX: raw.labelX ?? polygonLabelAnchor(nodes).x,
    labelY: raw.labelY ?? polygonLabelAnchor(nodes).y
  };
}

/**
 * Fills in the fields the monolith omits for a background image. `width === 0` is preserved verbatim — it is the
 * meaningful "not placed yet" signal, not a missing value (see {@link FloorPlanBackground.width}).
 */
export function normalizeBackground(raw: Partial<FloorPlanBackground>): FloorPlanBackground {
  return {
    id: raw.id ?? '',
    fileName: raw.fileName ?? '',
    contentType: raw.contentType ?? '',
    sizeBytes: raw.sizeBytes ?? 0,
    x: raw.x ?? 0,
    y: raw.y ?? 0,
    width: raw.width ?? 0,
    // A zero opacity would render an invisible image that the admin cannot find to fix; treat only a real number
    // as authoritative and default to fully opaque.
    opacity: typeof raw.opacity === 'number' && raw.opacity > 0 ? Math.min(raw.opacity, 1) : 1,
    url: raw.url ?? '/booking/FloorPlan/Image'
  };
}

/** Default footprint (in floor-plan units, i.e. centimetres) of a newly placed table — 1 m square. */
export const DEFAULT_TABLE_SIZE = 100;

/**
 * The colour a new area gets, matching the backend's own default (`Shared.Core.Validation.HexColor.Primary`) so a
 * draft area drawn client-side renders in the colour the server will store for it.
 */
export const DEFAULT_AREA_COLOR = '#C50E6C';

/**
 * The accent of the Tischplan editor's own chrome — the design handover's `#C50E6C`.
 *
 * **Deliberately not** this app's `theme-primary-600` (`#db2777`). The editor is a faithful migration of a design
 * component that ships its own accent, and the handover exposes it as its only prop (`accent`, §10). Everything in
 * that screen — title mark, selection highlights, the origin crosshair, the primary buttons, the focus ring inside
 * the inspector — uses this value.
 */
export const EDITOR_ACCENT = '#C50E6C';

/**
 * The colour palette offered in the area inspector — the handover's own five swatches (§5), in its own order.
 *
 * Muted, distinguishable hues that read as "rooms" rather than as status colours. The platform primary is
 * deliberately *not* among them: the handover's palette is exactly these five, and adding a sixth would make the
 * swatch row a different control from the one that was designed.
 */
export const AREA_COLOR_SWATCHES: readonly string[] = [
  '#8FB9A6', '#CBB790', '#9AB0C8', '#C8A0B6', '#B7B2A8'
];

/**
 * The colour a **freshly drawn** area gets, matching the handover's `finishZone()`. Distinct from
 * {@link DEFAULT_AREA_COLOR}, which is the *backend's* fallback for a row that carries no colour at all.
 */
export const NEW_AREA_COLOR = '#9AB0C8';

/** Whether a value is a `#RRGGBB` string — the exact shape the backend's `HexColor` validator accepts. */
export function isHexColor(value: string | null | undefined): boolean {
  return /^#[0-9a-fA-F]{6}$/.test((value ?? '').trim());
}

/** Smallest footprint a table may be resized to — below this the label no longer fits. */
export const MIN_TABLE_SIZE = 40;

/** Fewest vertices that make a polygon; a "line" area cannot be closed. */
export const MIN_AREA_NODES = 3;

/**
 * Projects a draft table onto its write model. Two invariants are enforced here rather than in the UI:
 * a draft id is dropped (so the upsert inserts), and `temporary` is forced to `false` — this screen only ever
 * manages the persistent catalog, and an accidental `true` would move the row into the ad-hoc booking flow.
 */
export function toTableUpdate(table: FloorTable): TableUpdate {
  return {
    ...(isDraftId(table.id) ? {} : {id: table.id}),
    name: table.name,
    tableType: table.tableType,
    tableShape: table.tableShape,
    seats: table.seats,
    x: table.x,
    y: table.y,
    width: table.width,
    height: table.height,
    description: table.description,
    temporary: false,
    areaId: table.areaId
  };
}

/**
 * Projects a draft area onto its write model (a draft id is dropped so the upsert inserts).
 *
 * Neither derived point is projected: both are server-derived and the write model has no field for them. An
 * unrecognisable colour is sent blank rather than rejected, which the server reads as "leave unspecified" and
 * answers by keeping the stored colour — the same blank-tolerant convention the other optional fields on this
 * wire use.
 */
export function toAreaUpdate(area: FloorArea): AreaUpdate {
  return {
    ...(isDraftId(area.id) ? {} : {id: area.id}),
    name: area.name,
    nodes: area.nodes.map(node => ({x: node.x, y: node.y})),
    color: isHexColor(area.color) ? area.color.trim() : ''
  };
}

/**
 * The next free numeric table name, so adding a table does not force the admin to invent one. Names that are not
 * plain numbers (e.g. "Bar 1") are ignored by the scan — they never collide with the generated sequence.
 */
export function nextTableName(tables: readonly FloorTable[]): string {
  const used = new Set(tables.map(t => Number.parseInt(t.name, 10)).filter(n => Number.isFinite(n)));
  let candidate = 1;
  while (used.has(candidate)) candidate++;
  return String(candidate);
}

/**
 * The visual centre of an area polygon — where its name label and centroid cross are drawn.
 *
 * Now the **area-weighted (shoelace) centroid**, not the arithmetic mean of the vertices it used to be: the backend
 * persists exactly that formula in `Area.CenterX`/`CenterY`, so mirroring it is what makes the live preview of an
 * area being dragged land where the next save will actually put it. Re-exported under the old name because it is
 * the same concept; the implementation lives in the pure geometry module with the rest of the canvas maths.
 */
export const polygonCenter = polygonCentroid;
